import {
  type FlightDetailsSegmentComponentProps,
  useFlightDetailsSegment as useCoreFlightDetailsSegment,
} from "@b2bportal/core-flights/overridable";
import { ScheduleChangeDisplayMode } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";

// Suffix the arrival time with "(+N day)" / "(+N days)" when the flight lands
// on a different calendar date than it departs. The core hook is the source of
// truth for everything else; we only decorate the already-formatted times so
// the layout (grid areas, span styling) is unchanged.
//
// In schedule-change views the displayed time can correspond to either the
// original or the updated schedule depending on `displayMode`, while
// `context.plusDays` always reflects the UPDATED schedule
// (`updatedSegment.plusDays ?? segment.plusDays` in the core hook). The suffix
// has to match the time it's attached to:
//   - prevArrivalTime is always the original  → use props.segment.plusDays
//   - currentArrivalTime is updated in `both`/`updated` modes but stays at
//     the original time in `original` mode    → branch on displayMode
// Without this guard, a same-day → next-day rebook would retroactively tag the
// original time as next-day.
//
// Note: this hook only decorates the time. The core FlightDetailsSegment
// component independently renders the cross-day body sentence (Trans of
// `flightArrivalDifferentDay.flightArrivesNextDay` / `flightArrivesOnDate` /
// `flightArrivesPlusDays`) when `context.plusDays !== 0`. Both signals are
// intentional — the HSBC i18n override on `flightArrivesNextDay` drops the
// "the next day" wording so the body reads as a complement to the inline
// suffix rather than a duplicate. Removing either side will leave a UX gap.
export const useFlightDetailsSegment = (
  props: FlightDetailsSegmentComponentProps
) => {
  const result = useCoreFlightDetailsSegment(props);
  const { t } = useI18nContext();

  const makeSuffix = (count: number) =>
    count > 0
      ? ` (${t("flightArrivalDifferentDay.plusDaysCount", {
          count,
          defaultValue: `+${count} day${count === 1 ? "" : "s"}`,
        })})`
      : "";

  const currentPlusDays = result.context.plusDays;
  const originalPlusDays = props.segment.plusDays ?? 0;
  if (currentPlusDays <= 0 && originalPlusDays <= 0) {
    return result;
  }

  // In `original` mode the core hook leaves currentArrivalTime as the original
  // time string, so the suffix attached to it must come from the original
  // schedule's plusDays, not the post-update count.
  const effectiveCurrentPlusDays =
    result.context.displayMode === ScheduleChangeDisplayMode.original
      ? originalPlusDays
      : currentPlusDays;

  const currentSuffix = makeSuffix(effectiveCurrentPlusDays);
  const prevSuffix = makeSuffix(originalPlusDays);

  return {
    ...result,
    context: {
      ...result.context,
      currentArrivalTime: `${result.context.currentArrivalTime}${currentSuffix}`,
      prevArrivalTime:
        result.context.prevArrivalTime != null
          ? `${result.context.prevArrivalTime}${prevSuffix}`
          : result.context.prevArrivalTime,
    },
  };
};
